/**
 * Asserts that a URL is safe to hand to `window.location.assign` for the
 * SSO initiation hop returned by HRD (`/sso/{protocol}/{org}/{idp}/login`).
 *
 * Why: HRD returns a same-origin path that points at our own backend's SSO
 * initiation endpoint (the real cross-origin redirect to the IdP is a 302
 * from there). If we ever passed a `javascript:` / `data:` value — or an
 * unexpected cross-origin host — to `location.assign`, it would run
 * attacker-controlled code in our origin or send the user to an attacker's
 * page. Resolve against `baseOrigin`, then require:
 *   - http(s) scheme
 *   - same origin as `baseOrigin`
 *
 * Throws on:
 *   - non-string input
 *   - empty string
 *   - URL that fails to parse (even relative against the base)
 *   - non-http(s) scheme (e.g. `javascript:`, `data:`, `file:`, `blob:`)
 *   - cross-origin result
 *
 * Returns the parsed `URL` on success.
 */
export function assertSafeSsoStartUrl(u: unknown, baseOrigin: string): URL {
  if (typeof u !== 'string' || u.length === 0) {
    throw new Error('invalid SSO URL: empty or non-string');
  }
  let parsed: URL;
  try {
    parsed = new URL(u, baseOrigin);
  } catch {
    throw new Error('invalid SSO URL: failed to parse');
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(`unsafe protocol: ${parsed.protocol}`);
  }
  if (parsed.origin !== new URL(baseOrigin).origin) {
    throw new Error(`unsafe cross-origin SSO URL: ${parsed.origin}`);
  }
  return parsed;
}
